import { selectChangeMessageForAgeRange } from '@UI/aside/asideAgeRange/asideAgeRangeSelectors';
import {
  selectChangeMessageForReferenceFramePicker,
  selectChangeMessageForZillGoals,
  selectWebFacetsConfig,
} from '@UI/aside/asideWebFacet/AsideWebFacetsSelectors';
import {
  type Content,
  type ContentHref,
  type ContentKey,
  ContentType,
} from '@generalTypes/apiTypes';
import type { RootState } from '@generalTypes/rootStateTypes';
import { createLRUSelector, keySelectorHref } from '@newStore/documentApi/documentApiHelpers';
import {
  selectApiWithPendingChanges,
  selectDocumentIsPartOfNodeHrefs,
  selectDocumentRoot,
  selectParentHrefs,
  selectPathToRootHrefs,
  selectWebPagePerContentMap,
} from '@newStore/documentApi/documentApiSelectors';
import { createTypedSelector } from '@newStore/genericHelpers';
import {
  selectIsFacetsAgeRangeValid,
  selectIsStartageLowerThanEndage,
} from '@newStore/validation/validationRules/ageRangeValid';
import {
  type EditAsideAgeRange,
  type EditAsideReferenceFramePicker,
  type EditAsideZillGoals,
  type EditComponentProperty,
  type FilterableEditComponent,
  type FilteredBuildingBlock,
  type FilteredEditComponent,
  type NodeType,
  type NodeTypeConfigApplied,
  isGenericRootNodeConfig,
  isHiddenNodeConfig,
  isRootNodeConfig,
} from '@nodeTypeConfig/configTypes';
import { proAccessRightsComponent } from '@nodeTypeConfig/helpers/editComponents/proAccessRightsComponent';
import { getNodeTypeConfig } from '@nodeTypeConfig/index';
import {
  getGenericNodeType,
  getNodeType,
  nodeTypeDefinitions,
} from '@nodeTypeConfig/nodeTypeConfig';
import { lruMemoize } from '@reduxjs/toolkit';
import { isEqual, omit } from 'lodash';
import { createCachedSelector } from 're-reselect';
import { shallowEqual } from 'react-redux';
import { ancestorTypeFilter } from './nodeTypeConfigHelpers';

export const selectDocumentRootType = createTypedSelector(
  [
    (state) => selectDocumentRoot(state),
    (state) =>
      state.documentUI.currentDocument
        ? selectWebPagePerContentMap(state)?.[state.documentUI.currentDocument]
        : undefined,
  ],
  (root, webPage) => {
    if (!root || Object.keys(root).length === 0) {
      // some weird bug, perhaps in reselect, where root becomes an empty object instead of null.
      return null;
    }
    return getNodeType(root, webPage, null);
  },
);

export const selectGenericDocumentRootType = createTypedSelector(
  [
    (state) =>
      state.documentUI.currentDocument &&
      state.documentApi.content[state.documentUI.currentDocument],
  ],
  (root) => {
    if (!root || Object.keys(root).length === 0) {
      // some weird bug, perhaps in reselect, where root becomes an empty object instead of null.
      return null;
    }
    return getGenericNodeType(root);
  },
);

export const selectGenericDocumentRootConfig = createTypedSelector(
  [selectGenericDocumentRootType],
  (type) => {
    if (!type || Object.keys(type).length === 0) {
      // some weird bug, perhaps in reselect, where root becomes an empty object instead of null.
      return null;
    }
    const nodeTypeConfig = getNodeTypeConfig(type);
    if (!isGenericRootNodeConfig(nodeTypeConfig)) {
      // can not happen this will always be a Non Extended RootNodeConfig
      // TODO remove if AppliedNodeConfig is Typed better with making distinction between Root, BuildingBLock, Hidden
      return null;
    }
    return nodeTypeConfig;
  },
);

export const selectDocumentHasIssuedField = createTypedSelector(
  [selectGenericDocumentRootConfig],
  (rootConfig) =>
    rootConfig?.edit?.some((field) => 'property' in field && field.property === 'issued') ??
    false,
);

export const selectAllNodeTypesMap = createLRUSelector(
  [
    (state) => selectApiWithPendingChanges(state).content,
    (state) => selectWebPagePerContentMap(state),
    (state) => selectDocumentRootType(state),
  ],
  (contentMap, webPageMap, documentType): Record<ContentHref, NodeType> => {
    return Object.fromEntries(
      Object.entries(contentMap).flatMap(([href, content]) => {
        let nodeType: NodeType;
        if (contentMap[href]) {
          nodeType = getNodeType(content, webPageMap[href], documentType);
        } else {
          // this can happen when:
          // - a global document is added to a download group. it temporarily has the relation without the content
          return [];
        }
        return [[href, nodeType]];
      }),
    );
  },
  {
    memoizeOptions: {
      resultEqualityCheck: shallowEqual,
    },
  },
);

export const selectNodeType = (state, href) => selectAllNodeTypesMap(state)[href];

export const selectWebsiteHrefForRoot = createTypedSelector(
  /**
   * why is this input selector using selectPathToRootHrefs to get the root?
   * this is for the edge case of teasers which are included in newsletters. there we want the websiteHref of the teaser, not the newsletter.
   * pathToRoot finds the real root, it does not follow IS_INCLUDED_IN relations.
   */
  [(state, href: ContentHref) => selectNodeType(state, selectPathToRootHrefs(state, href).at(-1))],
  (rootNodeType) => {
    const rootConfig = getNodeTypeConfig(rootNodeType);
    if ('extends' in rootConfig && rootConfig.extends) {
      const baseRootConfig = getNodeTypeConfig(rootConfig.extends);
      if ('websiteHref' in baseRootConfig) {
        return baseRootConfig.websiteHref;
      }
    } else if ('websiteHref' in rootConfig) {
      return rootConfig.websiteHref;
    }
    return null;
  },
);

/**
 * [PERF] selectAppliedNodeConfig has been called 201530 times. Total time spent: 392.20000000298023 ms.
   [PERF] selectAppliedNodeConfig Average time spent per call: 0.0019461122413684327 ms. Median time: 0 ms. Max time: 5.899999998509884 ms.
 */

const facetAgeRangeComponentConfig: EditAsideAgeRange = {
  component: 'ageRange',
  reactComponent: 'AsideAgeRange',
  properties: ['startage', 'endage'],
  label: 'Facet: Leeftijd',
  asideChangeMessageSelector: selectChangeMessageForAgeRange,
  validations: [selectIsStartageLowerThanEndage, selectIsFacetsAgeRangeValid],
};

const facetZillGoalsComponentConfig: EditAsideZillGoals = {
  label: 'Facet: Zill doelen selecteren',
  component: 'zillGoals',
  reactComponent: 'AsideZillGoals',
  asideChangeMessageSelector: selectChangeMessageForZillGoals,
};

export const selectAppliedNodeConfig = createCachedSelector(
  [
    (state, href: ContentHref) => selectNodeType(state, href),
    (state, href) => selectPathToRootHrefs(state, href),
    (state) => selectAllNodeTypesMap(state),
    (state) => selectDocumentRootType(state),
    (state, href) => selectWebFacetsConfig(state, href), // select parent webconfig
  ],
  (
    nodeType,
    pathToRootHrefs,
    allNodeTypesMap,
    rootType,
    webFacetsConfig,
  ): NodeTypeConfigApplied => {
    const rootConfig = rootType && getNodeTypeConfig(rootType);

    const fullConfig = getNodeTypeConfig(nodeType);

    // we use the audienceTab of the config, if it is defined, otherwise, use the default which is the root.
    const audienceTab =
      ('audienceTab' in fullConfig && fullConfig.audienceTab) ||
      (rootConfig && 'audienceTab' in rootConfig && rootConfig.audienceTab);

    if (isHiddenNodeConfig(fullConfig)) {
      // hidden nodes do not have edit or buildingBlocks and are never extended.
      return { ...fullConfig, edit: [], buildingBlocks: [] };
    }

    let filteredComponents = fullConfig.edit
      // .filter((c) => c) // because of circular imports, we can get undefined here. remove when no longer nescessary.
      .filter((c) => ancestorTypeFilter(c, pathToRootHrefs, allNodeTypesMap));

    // check for facet components and add them to the edit config
    if (webFacetsConfig.length) {
      const editComponentsToAdd: FilterableEditComponent[] = webFacetsConfig.flatMap((facet) => {
        if (facet.component === 'AGE_RANGE_SLIDER') {
          return facetAgeRangeComponentConfig;
        }

        if (facet.component === 'ZILL_GOAL_SELECTOR') {
          return facetZillGoalsComponentConfig;
        }

        if (facet.component !== 'SELECT_FROM_REFERENCE_FRAME' || !facet.source) {
          return [];
        }

        const facetReferenceFramePicker: EditAsideReferenceFramePicker = {
          label: `Facet: ${facet.label}`,
          component: 'referenceFramePicker',
          reactComponent: 'AsideReferenceFramePicker',
          asideChangeMessageSelector: selectChangeMessageForReferenceFramePicker,
          options: {
            types: [ContentType.THEME],
            referenceFrameHref: facet.source.href as ContentHref,
          },
        };
        return facetReferenceFramePicker;
      });

      if (editComponentsToAdd.length) {
        const facetIndex = filteredComponents.findIndex(
          (item) => item.component === 'webFacetsPlaceHolder',
        );
        if (facetIndex !== -1) {
          filteredComponents = [
            ...filteredComponents.slice(0, facetIndex),
            ...editComponentsToAdd,
            ...filteredComponents.slice(facetIndex + 1),
          ];
        } else {
          filteredComponents.push(...editComponentsToAdd);
        }
      }
    }

    const newEdit = filteredComponents.map((item) => {
      const filtered = omit(item, [
        'showInAncestorTypes',
        'hideInAncestorTypes',
      ]) as FilteredEditComponent;
      return filtered;
    });

    const filteredBuildingBlocks =
      fullConfig.buildingBlocks?.filter((c) => {
        return ancestorTypeFilter(c, pathToRootHrefs, allNodeTypesMap);
      }) || [];

    const newBuildingBlocks = filteredBuildingBlocks.map((item) => {
      const {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        showInAncestorTypes,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        hideInAncestorTypes,
        ...rest
      } = item;
      return rest;
    }) as Array<FilteredBuildingBlock>;

    const filteredConfig: NodeTypeConfigApplied = {
      ...fullConfig,
      edit: newEdit,
      buildingBlocks: newBuildingBlocks,
    };

    if (audienceTab) {
      if (fullConfig.addAccessRightsToAudienceTab) {
        const accessRightsComponent =
          typeof fullConfig.addAccessRightsToAudienceTab === 'object'
            ? fullConfig.addAccessRightsToAudienceTab
            : proAccessRightsComponent;
        filteredConfig.audienceTab = [...audienceTab, accessRightsComponent];
      } else {
        filteredConfig.audienceTab = audienceTab;
      }
    }

    if ('onEditIncludedNode' in filteredConfig) {
      // the onEditIncludedNode function should only be there for when the node is included in another doc.
      // If the node is the root, we should not have this function.
      if (nodeType === rootType) {
        delete filteredConfig.onEditIncludedNode;
      }
    }

    return filteredConfig;
  },
  {
    memoize: lruMemoize,
    argsMemoize: lruMemoize,
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      // (a, b) => {
      //   const res = shallowEqual(a.edit, b.edit);
      //   return res;
      // },
    },
  },
)(keySelectorHref);

// [PERF] Average time spent per call: 0.0033956613586068603 ms.
// [PERF] Average time spent per call: 0.0034193167688135804 ms.

// with LRU
// [PERF] Average time spent per call: 0.0022269005671806553 ms.
// [PERF] Average time spent per call: 0.002253639944860298 ms.

export const selectTypeNameSingle = (state: RootState, href: ContentHref) => {
  return selectAppliedNodeConfig(state, href).information.single;
};

export const selectEditConfigForNode = (
  state: RootState,
  href: ContentHref,
): FilteredEditComponent[] => {
  return selectAppliedNodeConfig(state, href).edit;
};

export const selectAudienceTabForNode = (state: RootState, href: ContentHref) => {
  return selectAppliedNodeConfig(state, href).audienceTab;
};

export const selectWebTabForNode = (state: RootState, href: ContentHref) => {
  return selectAppliedNodeConfig(state, href).webTab;
};

export const selectBuildingBlocksForNode = (
  state: RootState,
  href: ContentHref,
): FilteredBuildingBlock[] => {
  return selectAppliedNodeConfig(state, href).buildingBlocks || [];
};

export const selectAppliedDocumentRootConfig = (state: RootState) => {
  const href = state.documentUI.currentDocument;
  if (!href) {
    return null;
  }
  const nodeTypeConfig = selectAppliedNodeConfig(state, href);
  if (!isRootNodeConfig(nodeTypeConfig)) {
    // will never happen TODO remove if AppliedNodeConfig is Typed better with making distinction between Root, BuildingBLock, Hidden
    return null;
  }
  return nodeTypeConfig;
};

export const selectFieldEditConfigForNode = (
  state: RootState,
  href: ContentHref,
  property: string,
): FilteredEditComponent | undefined => {
  return selectAppliedNodeConfig(state, href).edit.find((c) => c.property === property);
};

export const selectContentNodesWithExtraProperties = (state: RootState) => {
  const { content } = selectApiWithPendingChanges(state);

  const items: Array<{
    item: Content;
    extraDefinedProperties: string[];
    parentHref: ContentHref | undefined;
    typeSingle: string;
  }> = [];

  const ignoredProperties = new Set([
    '$$meta',
    'key',
    'type',
    'tags',
    'created',
    'modified',
    'importance',
    'attachments',
    'language',
    // legacy fields
    'mainstructures',
    'outypes',
    'deepening',
    'mandatory',

    'accessRights',
    '$$relationsFrom',
    '$$relationsTo',
    'deleteProposal', // added in proposals
  ]);

  const documentHrefs: Set<ContentHref> = selectDocumentIsPartOfNodeHrefs(state);
  // get ALL content hrefs that we have. for a ZILL_PRACTICAL_EXAMPLE we are also dumping ZILL_ILLUSTRATIONS into the content, which is not part of this document.
  Object.entries(content).forEach(([href, contentItem]) => {
    if (!documentHrefs.has(href as ContentHref)) {
      return;
    }

    const appliedConfig = selectAppliedNodeConfig(state, href as ContentHref);
    if ('hideInDocument' in appliedConfig && appliedConfig.hideInDocument) {
      return;
    }

    const genericTypeConfig =
      'extends' in appliedConfig && getNodeTypeConfig(appliedConfig.extends);

    const properties = new Set(
      appliedConfig.edit
        .flatMap((c) => c.property || ('properties' in c && c.properties) || c.component)
        .map((z) => z.split('.')[0].replace('$$', '').replace('[0]', '')), // for path-based properties, only take the first part, and remove the array index
    );

    if (appliedConfig.audienceTab) {
      appliedConfig.audienceTab.forEach((item) => properties.add(item.property as string));
    }

    // take all properties of the node definition as well (relevant for specificForAN)
    const type = selectNodeType(state, href as ContentHref);
    Object.keys(nodeTypeDefinitions[type].node).forEach((key) => properties.add(key));

    // also check createDefaults that are unchangable, like educationsorts
    if ('createDefaults' in appliedConfig) {
      Object.keys(appliedConfig.createDefaults).forEach((key) => properties.add(key));
    }

    if (genericTypeConfig && 'createDefaults' in genericTypeConfig) {
      Object.keys(genericTypeConfig.createDefaults).forEach((key) => properties.add(key));
    }

    const extraDefinedProperties = Object.keys(contentItem).filter(
      (key) =>
        !ignoredProperties.has(key) &&
        !key.startsWith('$$') && // ignore $$ fields since they are read only
        contentItem[key] !== null &&
        contentItem[key] !== undefined &&
        contentItem[key] !== '' &&
        !(Array.isArray(contentItem[key]) && contentItem[key].length === 0) &&
        !properties.has(key as EditComponentProperty),
    );

    if (extraDefinedProperties.length > 0) {
      items.push({
        item: contentItem,
        parentHref: selectParentHrefs(state, contentItem.$$meta.permalink).slice(-1)[0],
        typeSingle: selectTypeNameSingle(state, contentItem.$$meta.permalink),
        extraDefinedProperties,
      });
    }
  });
  return items;
};

export const selectPatchesToCleanNodes = createTypedSelector(
  [(state) => selectContentNodesWithExtraProperties(state)],
  (items) => {
    const patches: Array<{
      key: ContentKey;
      property: string;
    }> = [];

    items.forEach(({ item: contentItem, extraDefinedProperties }) => {
      if (extraDefinedProperties.length > 0) {
        extraDefinedProperties.forEach((property) => {
          patches.push({
            key: contentItem.key,
            property,
          });
        });
      }
    });

    return patches;
  },
);
